import { useMemo, useState, useEffect } from "react";
import { diffWords } from "diff";
import { ArrowUUpLeft } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Pane({ parts, side, version }) {
  return (
    <div className="flex min-w-0 flex-col rounded-2xl border border-black/5 bg-[#FAFAFC]">
      <div className="flex items-center justify-between border-b border-black/5 px-4 py-2.5 text-xs text-mist">
        <span className="font-medium text-ink">v{version.idx + 1}</span>
        <span>score {version.v.quality_score} · {version.v.model}</span>
      </div>
      <pre data-testid={`diff-pane-${side}`} className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-[12px] leading-relaxed text-ink">
        {parts.map((p, i) => {
          if (side === "left" && p.added) return null;
          if (side === "right" && p.removed) return null;
          const cls = p.removed ? "bg-[#FFE5E7] text-[#B3001B] line-through decoration-[#B3001B]/40" : p.added ? "bg-[#DFF6E4] text-[#0B6B2A]" : "";
          return <span key={i} className={cls}>{p.value}</span>;
        })}
      </pre>
    </div>
  );
}

function VersionSelect({ value, onChange, versions, testId }) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger data-testid={testId} className="h-9 w-32 rounded-full border-black/10 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="rounded-xl">
        {versions.map((_, i) => <SelectItem key={i} value={String(i)}>Version {i + 1}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function VersionDiff({ item, open, onOpenChange, initialTarget, onRollback }) {
  const versions = useMemo(() => item?.versions || [], [item]);
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(0);

  useEffect(() => {
    if (!open || !item) return;
    const r = initialTarget ?? Math.max(0, versions.length - 1);
    const l = item.active_version === r ? Math.max(0, r - 1) : item.active_version;
    setLeft(l);
    setRight(r === l && versions.length > 1 ? Math.min(versions.length - 1, l + 1) : r);
  }, [open, item, initialTarget]); // eslint-disable-line

  const parts = useMemo(() => (versions.length ? diffWords(versions[left]?.prompt || "", versions[right]?.prompt || "") : []), [versions, left, right]);
  const added = parts.filter((p) => p.added).reduce((a, p) => a + p.value.split(/\s+/).filter(Boolean).length, 0);
  const removed = parts.filter((p) => p.removed).reduce((a, p) => a + p.value.split(/\s+/).filter(Boolean).length, 0);
  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="version-diff-dialog" className="max-w-5xl rounded-3xl p-6 sm:p-8">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-semibold text-ink">Compare versions</DialogTitle>
          <DialogDescription className="text-sm text-mist">See exactly what changed before you roll back.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <VersionSelect value={left} onChange={setLeft} versions={versions} testId="diff-left-select" />
          <span className="text-mist">→</span>
          <VersionSelect value={right} onChange={setRight} versions={versions} testId="diff-right-select" />
          <span data-testid="diff-stats" className="ml-auto flex gap-2">
            <span className="rounded-full bg-[#DFF6E4] px-2.5 py-1 font-medium text-[#0B6B2A]">+{added} words</span>
            <span className="rounded-full bg-[#FFE5E7] px-2.5 py-1 font-medium text-[#B3001B]">−{removed} words</span>
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Pane parts={parts} side="left" version={{ idx: left, v: versions[left] || {} }} />
          <Pane parts={parts} side="right" version={{ idx: right, v: versions[right] || {} }} />
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="h-10 rounded-full bg-black/5 px-5 text-sm font-medium text-ink hover:bg-black/10" data-testid="diff-cancel-button">Close</button>
          <button
            data-testid="diff-rollback-button"
            disabled={right === item.active_version}
            onClick={() => onRollback(right)}
            className="flex h-10 items-center gap-2 rounded-full bg-ink px-5 text-sm font-semibold text-white hover:bg-black disabled:opacity-40"
          >
            <ArrowUUpLeft size={15} weight="bold" /> {right === item.active_version ? "Already active" : `Roll back to v${right + 1}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
